<template>
  <tr>
    <td class="text-no-wrap" v-text="item.item_number" />
    <td class="text-left">
      <div v-text="item.description" />
      <lead-time-chip v-if="forQuote" class="mt-2" info :lead-time="item.lead_time" />
      <div class="text-mid-grey" v-text="item.comment" />
      <div v-if="bom">
        <nuxt-link
          class="text-decoration-none"
          :to="{ name: 'projects-id-boms-bomId-index', params: { id: bom.project, bomId: bom.bill_of_materials } }"
        >
          <v-icon icon="mdi-link" />
          {{ bom.bill_of_materials_title }}
        </nuxt-link>
      </div>
      <div v-if="withReceived && partialReceipt" class="text-primary">
        Incomplete
      </div>
    </td>
    <td v-if="equipment.length > 0">
      {{ itemEquipment?.equipment_number ?? '' }}
      <div class="text-mid-grey" v-text="itemEquipment?.description" />
    </td>
    <order-rfq-columns
      v-if="fromBom"
      :item="item"
      :boms="boms"
      :quotes="quotes"
    />
    <template v-else>
      <td v-if="withDuration">
        <span v-if="rental">
          {{ item.rental_duration }} {{ item.rental_duration_unit }}
        </span>
        <span v-else>
          -
        </span>
      </td>
      <td v-if="!isSingleItem">
        <div class="text-left">
          {{ Number(item.quantity) }} <span v-if="!forQuote" v-text="item.unit" />
        </div>
      </td>
      <td v-if="forQuote" v-text="item.unit" />
      <td v-if="releaseRequests.length > 0">
        <span :class="{ 'text-primary': Number(item.quantity) !== released }" v-text="released" />
        {{ item.unit }}
      </td>
      <td v-if="withReceived">
        <div class="text-left">
          <span :class="{ 'text-primary': partialReceipt }" v-text="Number(item.received_quantity)" />
          {{ item.unit }}
        </div>
      </td>
      <td v-if="!isSingleItem" class="text-no-wrap text-right">
        <span :title="hasRentalPrice ? `per ${item.rental_price.per}` : undefined">
          {{ currency(unitPrice) }}
        </span>
      </td>
      <td class="text-no-wrap text-right">
        {{ currency(subTotal) }}
      </td>
      <td v-if="!forQuote && !inventory" v-text="costCode" />
    </template>
  </tr>
</template>

<script setup lang='ts'>
import orderRfqColumns from '@/components/orders/order-rfq-columns.vue'
import LeadTimeChip from '@/components/items/lead-time-chip.vue'
import { currency } from '~/models/filters'
import type { Equipment, LineItem, ReleaseRequest } from '@quotetome/materials-api'

export interface Props {
  boms?: any[],
  costCodes?: any[]
  equipment: Equipment[],
  forQuote?: boolean,
  fromBom?: boolean,
  inventory?: boolean,
  isSingleItem?: boolean,
  item: LineItem,
  quotes?: any[],
  releaseRequests?: ReleaseRequest[],
  withDuration?: boolean,
  withReceived?: boolean,
}

const props = withDefaults(defineProps<Props>(), {
  boms: () => [],
  costCodes: () => [],
  quotes: () => [],
  releaseRequests: () => [],
})

const bom = computed(() => (props.boms.find(bo => (
  bo.purchase_order_item?.id === props.item.id
  || bo.order_item_identifier === props.item.internal_identifier
))))

const costCode = computed(() => {
  const description = props.costCodes.find(cc => cc.cost_code === props.item.cost_code)?.description

  if (description) {
    return `${props.item.cost_code} ${description}`
  }

  return props.item.cost_code
})

const itemEquipment = computed(() => props.equipment.find(e => e.id === props.item.equipment))

const partialReceipt = computed(() => Number(props.item.quantity) !== Number(props.item.received_quantity))

const rental = computed(() => !!props.item.rental_duration)

const hasRentalPrice = computed(() => rental.value && props.item.rental_price)

const subTotal = computed(() => {
  const price = (hasRentalPrice.value ? props.item.rental_price.price : props.item.unit_price) || 0
  return Number(props.item.quantity) * price
})

const unitPrice = computed(() => (hasRentalPrice.value ? props.item.rental_price.rate : props.item.unit_price))

const released = computed(() => props.releaseRequests.reduce((acc, request) => {
  return acc + request.items.filter(i => i.item === props.item.internal_identifier).reduce((acc2, i) => {
    return acc2 + Number(i.quantity)
  }, 0)
}, 0))
</script>
